import React, { useState, useEffect } from "react";
import { Trash2, X, Plus } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import SearchableCodeSelect from "../../../common/SearchableCodeSelect";

// Work orders have order_number/description, not the picker's default code.
const WO_OPTION = {
  getOptionValue: (o) => o.order_number,
  getOptionKey: (o) => o.uuid || o.order_number,
  formatOptionLabel: (o) =>
    o.description ? `${o.order_number} - ${o.description}` : o.order_number,
  renderOption: (o) => (
    <>
      <div className="searchable-code-select__option-code">
        {o.order_number}
      </div>
      <div className="searchable-code-select__option-name">{o.description}</div>
    </>
  ),
};

// Service persons have serviceperson/name, not the picker's default code.
const TECH_OPTION = {
  getOptionValue: (o) => o.serviceperson,
  getOptionKey: (o) => o.uuid || o.serviceperson,
  formatOptionLabel: (o) =>
    o.name ? `${o.serviceperson} - ${o.name}` : o.serviceperson,
  renderOption: (o) => (
    <>
      <div className="searchable-code-select__option-code">
        {o.serviceperson}
      </div>
      <div className="searchable-code-select__option-name">{o.name}</div>
    </>
  ),
};

// NOTE: shape is inferred from CE's CAPIFilterSettings_ServiceWorkOrders
// (apifilters.h) — MAPRANGE(codes) + status flag (6 states) + date-after +
// assigned-date range + tech-assigned range. Not yet verified against a
// live GET /group/{uuid}/filters payload.
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "open_hold", label: "Open Hold" },
  { value: "ready_hold", label: "Ready Hold" },
  { value: "ready", label: "Ready" },
  { value: "invoiced", label: "Invoiced" },
];

function blankRule() {
  return {
    filter_type: "include",
    service_work_orders: {
      from: "",
      to: "",
      status: "open",
      date_after: "",
      assigned_from_date: "",
      assigned_to_date: "",
      from_tech_assigned: "",
      to_tech_assigned: "",
    },
  };
}

export default function ServiceWorkOrdersFilterModal({
  open,
  groupUuid,
  companyUuid,
  filter,
  onSave,
  onClose,
  readOnly = false,
}) {
  const [rules, setRules] = useState([blankRule()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = filter?.rules;
    setRules(
      existing && existing.length > 0
        ? existing.map((r) => ({
            filter_type: r.filter_type || "include",
            service_work_orders: {
              from: r.service_work_orders?.from ?? "",
              to: r.service_work_orders?.to ?? "",
              status: r.service_work_orders?.status ?? "open",
              date_after: r.service_work_orders?.date_after ?? "",
              assigned_from_date:
                r.service_work_orders?.assigned_from_date ?? "",
              assigned_to_date: r.service_work_orders?.assigned_to_date ?? "",
              from_tech_assigned:
                r.service_work_orders?.from_tech_assigned ?? "",
              to_tech_assigned: r.service_work_orders?.to_tech_assigned ?? "",
            },
          }))
        : [blankRule()]
    );
    setError(null);
  }, [open, filter]);

  if (!open) return null;

  function updateRule(index, field, value) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === index
          ? {
              ...r,
              service_work_orders: { ...r.service_work_orders, [field]: value },
            }
          : r
      )
    );
  }

  function updateFilterType(index, value) {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, filter_type: value } : r))
    );
  }

  function removeRule(index) {
    setRules((prev) =>
      prev.length === 1 ? [blankRule()] : prev.filter((_, i) => i !== index)
    );
  }

  function handleSave() {
    const cleanedRules = rules.map((r) => ({
      filter_type: r.filter_type,
      service_work_orders: {
        from: r.service_work_orders.from || null,
        to: r.service_work_orders.to || null,
        status: r.service_work_orders.status || null,
        date_after: r.service_work_orders.date_after || null,
        assigned_from_date: r.service_work_orders.assigned_from_date || null,
        assigned_to_date: r.service_work_orders.assigned_to_date || null,
        from_tech_assigned: r.service_work_orders.from_tech_assigned || null,
        to_tech_assigned: r.service_work_orders.to_tech_assigned || null,
      },
    }));

    onSave({ resource: "svcwrko", mode: "filtered", rules: cleanedRules });
  }

  return (
    <div className="jfm-overlay" onClick={onClose}>
      <div className="jfm-container" onClick={(e) => e.stopPropagation()}>
        <div className="jfm-header">
          <span className="jfm-title">Service Work Orders Filters</span>
          <button className="jfm-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="jfm-body">
          <fieldset className="jfm-fieldset" disabled={readOnly}>
            {rules.map((rule, idx) => (
              <div key={idx} className="eqfm-row">
                <select
                  className="jfm-type-select"
                  value={rule.filter_type}
                  onChange={(e) => updateFilterType(idx, e.target.value)}
                >
                  <option value="include">Include</option>
                  <option value="exclude">Exclude</option>
                </select>

                <div className="eqfm-fields">
                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>From WO</label>
                      <SearchableCodeSelect
                        value={rule.service_work_orders.from}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={
                          FieldConfigurationAPI.getServiceWorkOrders
                        }
                        getOptionValue={WO_OPTION.getOptionValue}
                        getOptionKey={WO_OPTION.getOptionKey}
                        formatOptionLabel={WO_OPTION.formatOptionLabel}
                        renderOption={WO_OPTION.renderOption}
                        emptyMessage="No service work orders found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "from", code)}
                      />
                    </div>
                    <div className="efm-field">
                      <label>To WO</label>
                      <SearchableCodeSelect
                        value={rule.service_work_orders.to}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={
                          FieldConfigurationAPI.getServiceWorkOrders
                        }
                        getOptionValue={WO_OPTION.getOptionValue}
                        getOptionKey={WO_OPTION.getOptionKey}
                        formatOptionLabel={WO_OPTION.formatOptionLabel}
                        renderOption={WO_OPTION.renderOption}
                        emptyMessage="No service work orders found"
                        disabled={readOnly}
                        onChange={(code) => updateRule(idx, "to", code)}
                      />
                    </div>
                    <div className="efm-field efm-field--status">
                      <label>Status</label>
                      <select
                        value={rule.service_work_orders.status || "open"}
                        onChange={(e) =>
                          updateRule(idx, "status", e.target.value)
                        }
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>Scheduled After</label>
                      <input
                        type="date"
                        value={rule.service_work_orders.date_after}
                        onChange={(e) =>
                          updateRule(idx, "date_after", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>Date Assigned From</label>
                      <input
                        type="date"
                        value={rule.service_work_orders.assigned_from_date}
                        onChange={(e) =>
                          updateRule(idx, "assigned_from_date", e.target.value)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>Date Assigned To</label>
                      <input
                        type="date"
                        value={rule.service_work_orders.assigned_to_date}
                        onChange={(e) =>
                          updateRule(idx, "assigned_to_date", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="eqfm-inputs-row">
                    <div className="efm-field">
                      <label>Tech Assigned From</label>
                      <SearchableCodeSelect
                        value={rule.service_work_orders.from_tech_assigned}
                        companyUuid={companyUuid}
                        placeholder="(first)"
                        fetchOptions={FieldConfigurationAPI.getServicePersons}
                        getOptionValue={TECH_OPTION.getOptionValue}
                        getOptionKey={TECH_OPTION.getOptionKey}
                        formatOptionLabel={TECH_OPTION.formatOptionLabel}
                        renderOption={TECH_OPTION.renderOption}
                        emptyMessage="No service persons found"
                        disabled={readOnly}
                        onChange={(code) =>
                          updateRule(idx, "from_tech_assigned", code)
                        }
                      />
                    </div>
                    <div className="efm-field">
                      <label>Tech Assigned To</label>
                      <SearchableCodeSelect
                        value={rule.service_work_orders.to_tech_assigned}
                        companyUuid={companyUuid}
                        placeholder="(last)"
                        fetchOptions={FieldConfigurationAPI.getServicePersons}
                        getOptionValue={TECH_OPTION.getOptionValue}
                        getOptionKey={TECH_OPTION.getOptionKey}
                        formatOptionLabel={TECH_OPTION.formatOptionLabel}
                        renderOption={TECH_OPTION.renderOption}
                        emptyMessage="No service persons found"
                        disabled={readOnly}
                        onChange={(code) =>
                          updateRule(idx, "to_tech_assigned", code)
                        }
                      />
                    </div>
                  </div>
                </div>

                {!readOnly && (
                  <button
                    className="jfm-delete"
                    onClick={() => removeRule(idx)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </fieldset>
        </div>

        {error && <div className="jfm-error">{error}</div>}

        <div className="jfm-footer">
          {readOnly ? (
            <div />
          ) : (
            <button
              className="jfm-add-row"
              onClick={() => setRules((prev) => [...prev, blankRule()])}
            >
              <Plus size={14} /> Add Row
            </button>
          )}
          <div className="jfm-footer-actions">
            {readOnly ? (
              <button className="jfm-cancel" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="jfm-cancel"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="jfm-save"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
